import {
  belopFraDesimaltekst,
  investeringKreverForklaring,
  periodenavn,
  styrerRapporten,
  type EndreInvestering,
  type InvesteringDto,
  type InvesteringsimportAnalyseDto,
  type InvesteringsoppfolgingDto,
  type Investeringsstatus,
  type Maned,
  type Meg,
  type MeldInvesteringsprognose,
  type Prognoserytme,
  type UtforInvesteringsimport,
} from '@es/shared';
import { kronerTilFelt, lesKroner } from '../kpi/kutt.js';

/** Investeringene gjennom året. Se `domain/investeringsoppfolging.ts`. */

export const STATUSFARGE: Record<Investeringsstatus, string> = {
  ikke_meldt: 'bg-flate-dempet text-tekst',
  merforbruk: 'bg-negativ-svak text-negativ',
  mindreforbruk: 'bg-varsel-svak text-varsel',
  i_rute: 'bg-positiv-svak text-positiv',
};

/** Siden er for økonomisjefen, controllerne og kommunalsjefene. */
export function serInvesteringene(meg: Meg): boolean {
  return styrerRapporten(meg) || meg.omradetilganger.length > 0;
}

/** «Prognose for september», eller hvorfor det ikke er noe å melde. */
export function periodetekst(rytme: Prognoserytme, periode: Maned | null): string {
  if (periode === null) return 'Regnskapet for investeringene er ikke ført for året ennå.';
  return `Prognosen meldes for ${periodenavn(rytme, periode)}.`;
}

export interface Meldefelt {
  readonly arsprognose: string;
  readonly sluttprognose: string;
  readonly forklaring: string;
}

/** Feltene fylt med forrige prognose, eller med budsjettet når ingen er meldt. */
export function tilMeldefelt(p: InvesteringDto): Meldefelt {
  if (p.prognose === null) {
    return {
      arsprognose: kronerTilFelt(p.budsjett),
      sluttprognose: kronerTilFelt(p.kostnadsramme),
      forklaring: '',
    };
  }
  return {
    arsprognose: kronerTilFelt(p.prognose.arsprognose),
    sluttprognose: kronerTilFelt(p.prognose.sluttprognose),
    forklaring: p.prognose.forklaring ?? '',
  };
}

/** Meldingen som sendes, eller hva som er galt med feltene. */
export function lesMelding(f: Meldefelt, ar: number): MeldInvesteringsprognose | string {
  const arsprognose = lesKroner(f.arsprognose);
  if (arsprognose === undefined) return 'Prognosen for året må være et beløp i kroner.';
  if (arsprognose === null) return 'Skriv inn prognosen for året.';
  const sluttprognose = lesKroner(f.sluttprognose);
  if (sluttprognose === undefined) return 'Sluttprognosen må være et beløp i kroner.';
  const forklaring = f.forklaring.trim();
  return { ar, arsprognose, sluttprognose, forklaring: forklaring === '' ? null : forklaring };
}

/** Om feltene slik de står, krever en forklaring. Ugyldige felt krever ingenting ennå. */
export function kreverForklaring(
  p: InvesteringDto,
  f: Meldefelt,
  terskel: InvesteringsoppfolgingDto['terskel'],
): boolean {
  const melding = lesMelding(f, 2000);
  if (typeof melding === 'string') return false;
  return investeringKreverForklaring(
    belopFraDesimaltekst(p.budsjett),
    belopFraDesimaltekst(melding.arsprognose),
    p.kostnadsramme === null ? null : belopFraDesimaltekst(p.kostnadsramme),
    melding.sluttprognose === null ? null : belopFraDesimaltekst(melding.sluttprognose),
    terskel === null
      ? null
      : { prosent: terskel.prosent, belop: belopFraDesimaltekst(terskel.belop) },
  );
}

export interface Innstillingsfelt {
  readonly navn: string;
  /** Tom er hele kommunen. */
  readonly omradeId: string;
  readonly kostnadsramme: string;
  readonly avsluttet: boolean;
}

export function tilInnstillinger(p: InvesteringDto): Innstillingsfelt {
  return {
    navn: p.navn,
    omradeId: p.omrade?.id ?? '',
    kostnadsramme: kronerTilFelt(p.kostnadsramme),
    avsluttet: p.avsluttet,
  };
}

export function lesInnstillinger(f: Innstillingsfelt, versjon: number): EndreInvestering | string {
  const navn = f.navn.trim();
  if (navn === '') return 'Prosjektet må ha et navn.';
  const kostnadsramme = lesKroner(f.kostnadsramme);
  if (kostnadsramme === undefined) return 'Rammen må være et beløp i kroner.';
  if (kostnadsramme?.startsWith('-') === true) {
    return 'Rammen kan ikke være negativ.';
  }
  return {
    navn,
    omradeId: f.omradeId === '' ? null : f.omradeId,
    kostnadsramme,
    avsluttet: f.avsluttet,
    versjon,
  };
}

/**
 * Radene som importeres. Et nytt prosjekt legges i området brukeren valgte for
 * det; tomt er hele kommunen. Et prosjekt som finnes, beholder sitt område.
 */
export function importrader(
  a: InvesteringsimportAnalyseDto,
  omrader: Readonly<Record<string, string>>,
): UtforInvesteringsimport['rader'] {
  return a.rader.map((r) => ({
    kode: r.kode,
    navn: r.navn,
    omradeId:
      r.finnes === null && (omrader[r.kode] ?? '') !== '' ? (omrader[r.kode] ?? null) : null,
    budsjett: r.budsjett,
    regnskap: r.regnskap,
  }));
}
